import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from '../../shared/components/toast/toast.service';

/**
 * Global error handler service that processes and displays application errors.
 * Implements Angular's ErrorHandler interface to catch and handle both HTTP and client-side errors.
 * Provides error debouncing to prevent duplicate error messages and handles specific HTTP status codes.
 */
@Injectable({
    providedIn: 'root',
})
export class GlobalErrorHandler implements ErrorHandler {
    private readonly toastService = inject(ToastService);
    private readonly router = inject(Router);

    private lastError: { message: string; timestamp: number } | null = null;
    private readonly DEBOUNCE_TIME = 2000; // 2 seconds

    /**
     * Handles any error thrown in the application.
     * Processes both HttpErrorResponse and standard Error objects.
     * Displays error messages using PrimeNG MessageService and handles special cases like 401 unauthorized.
     *
     * @param error - The error object to handle (either HttpErrorResponse or Error)
     */
    handleError(error: Error | HttpErrorResponse) {
        console.error('An error occurred:', error);

        // Remove ngZone.run wrapper and handle error directly
        const errorMessage = error instanceof HttpErrorResponse ? this.getHttpErrorMessage(error) : this.getClientErrorMessage(error);

        // Check if this is a duplicate error within the debounce time
        if (this.isDuplicateError(errorMessage)) {
            return;
        }

        // Update last error
        this.lastError = {
            message: errorMessage.detail,
            timestamp: Date.now(),
        };

        // Redirect to auth page when error is 401 (unauthorized)
        if (error instanceof HttpErrorResponse && error.status === 401) {
            if (this.router.url.includes('/conf')) {
                this.router.navigate(['/conf/auth'], {
                    queryParams: { logout: true },
                });
                return;
            } else {
                const currentLang = this.router.url.split('/')[1] || 'de';
                this.router.navigate([currentLang, 'auth'], {
                    queryParams: { logout: true },
                });
                return;
            }
        }

        // Use the complete error message object when calling toastService
        this.toastService.error(errorMessage.detail, errorMessage.summary, {
            life: errorMessage.life,
        });
    }

    /**
     * Checks if an error message is a duplicate of the last shown error within the debounce time window.
     *
     * @param newError - The new error message to check
     * @returns boolean indicating if the error is a duplicate
     */
    private isDuplicateError(newError: { detail: string }): boolean {
        if (!this.lastError) {
            return false;
        }

        const timeSinceLastError = Date.now() - this.lastError.timestamp;
        return timeSinceLastError < this.DEBOUNCE_TIME && this.lastError.message === newError.detail;
    }

    /**
     * Formats HTTP errors into user-friendly messages based on status codes.
     * Handles specific cases for 404, 403, 401, and 500+ status codes.
     *
     * @param error - The HTTP error response to process
     * @returns Formatted error message object with severity, summary, and detail
     */
    private getHttpErrorMessage(error: HttpErrorResponse) {
        let errorMessage = {
            detail: 'Ein Fehler ist aufgetreten.',
            summary: 'Fehler',
        };

        if (error.status === 404) {
            errorMessage = {
                detail: 'Die angeforderte Seite konnte nicht gefunden werden. Bitte überprüfen Sie die URL oder kehren Sie zur Startseite zurück.',
                summary: 'Seite nicht gefunden',
            };
        } else if (error.status === 403) {
            errorMessage = {
                detail: 'Sie haben keine Berechtigung, auf diese Seite zuzugreifen.',
                summary: 'Zugriff verweigert',
            };
        } else if (error.status === 401) {
            errorMessage = {
                detail: 'Bitte melden Sie sich an, um fortzufahren.',
                summary: 'Anmeldung erforderlich',
            };
        } else if (error.status >= 500) {
            errorMessage = {
                detail: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
                summary: 'Fehler',
            };
        }

        return {
            severity: 'error',
            life: 5000,
            ...errorMessage,
        };
    }

    /**
     * Formats client-side errors into user-friendly messages.
     *
     * @param error - The client-side error to process
     * @returns Formatted error message object with severity, summary, and detail
     */
    private getClientErrorMessage(error: Error) {
        return {
            error,
            severity: 'error',
            summary: 'Anwendungsfehler',
            detail: 'Ein unerwarteter Fehler ist aufgetreten. Bitte laden Sie die Seite neu.',
            life: 5000,
        };
    }
}
